import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authService } from '../../services/authService';
import { apiJson, apiVoid } from './apiFetch';
import { queryKeys } from '../../lib/queryKeys';
import type { CanvasserAnalyticsFilters } from '../../lib/queryKeys';
import { useCampaignStore } from '../../stores';
import type { AttentionNeededResponse, DismissFlagRequest } from '@signalvote/shared';
import { buildQueryString } from './queryUtils';

export function useAttentionNeeded(
  filters?: CanvasserAnalyticsFilters,
  includeDismissed?: boolean
) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.analytics.attentionNeeded(filters, includeDismissed),
    queryFn: async () => {
      const qs = buildQueryString({
        type: filters?.type,
        vendorId: filters?.vendorId,
        teamId: filters?.teamId,
        dateFrom: filters?.dateFrom,
        dateTo: filters?.dateTo,
        includeDismissed: includeDismissed ? 'true' : undefined,
      });
      return apiJson<AttentionNeededResponse>(`/api/analytics/attention-needed${qs}`, {
        error: 'Failed to fetch attention needed',
      });
    },
    enabled: authService.isAuthenticated() && !!selectedCampaign,
    staleTime: 2 * 60 * 1000,
  });
}

export function useDismissFlag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: DismissFlagRequest) =>
      apiVoid('/api/analytics/dismiss-flag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        json: request,
        error: 'Failed to dismiss flag',
      }),
    onMutate: async (request) => {
      // Only cancel attention-needed queries, not all analytics queries
      await queryClient.cancelQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) && query.queryKey.includes('attentionNeeded'),
      });

      const previousData = queryClient.getQueriesData<AttentionNeededResponse>({
        predicate: (query) =>
          Array.isArray(query.queryKey) && query.queryKey.includes('attentionNeeded'),
      });

      queryClient.setQueriesData<AttentionNeededResponse>(
        {
          predicate: (query) =>
            Array.isArray(query.queryKey) && query.queryKey.includes('attentionNeeded'),
        },
        (old) => {
          if (!old || !('items' in old)) return old;
          return {
            ...old,
            items: old.items.map((item) =>
              item.key === request.flagKey ? { ...item, isDismissed: true } : item
            ),
            dismissedCount: old.dismissedCount + 1,
          };
        }
      );

      return { previousData };
    },
    onError: (_err, _request, context) => {
      if (context?.previousData) {
        for (const [queryKey, data] of context.previousData) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) && query.queryKey.includes('attentionNeeded'),
      });
    },
  });
}
